import { useCallback, useMemo, type MouseEvent, type ReactNode } from 'react';
import { ClockCountdownIcon, TrashIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { GenericEventFeed } from './GenericEventFeed';
import { useReportEventsData } from './hooks';
import { useOpenInNewWindow } from '@/hooks';
import {
    RemoveReportEventConfirmationModal,
    type CardMetadataItem,
} from '@/components';
import { useReportEventRemoval } from '@/features/ReportDetail/hooks/useReportEventRemoval';
import {
    parseSortOption,
    URL_PARAM,
} from '@/features/ReportDetail/hooks/useReportViewControls';
import { sortReportEvents } from '@/features/ReportDetail/utils/sortReportEvents';
import { AppRoutes } from '@/constants/routes';
import { useEventFeedStore } from '@/stores/event-feed.store';
import type { Event } from '@/types/event';
import type { ReportEvent } from '@/types/report';
import { formatEventTimeCompact, isVideoTimeRange } from '@/utils/date';
import { formatEventTime } from '@/utils/time';
import { getReportEventDisplayConfidence } from '@/utils/report-event-confidence';
import { getReportEventTime } from '@/utils/report-event-time';
import type { FeedEmptyStateProps } from './components';

interface ReportEventsFeedProps {
    reportId: string;
    titleContent?: ReactNode;
    emptyState?: FeedEmptyStateProps;
}

function reportEventTimeMetadata(
    reportEvent: ReportEvent,
): CardMetadataItem | undefined {
    const eventTime = getReportEventTime(reportEvent);
    if (eventTime === undefined || !isVideoTimeRange(reportEvent.sourceType)) {
        return undefined;
    }
    return {
        icon: ClockCountdownIcon,
        text:
            reportEvent.videoEndSeconds !== null
                ? formatEventTimeCompact(eventTime, reportEvent.videoEndSeconds)
                : formatEventTime(eventTime),
    };
}

function reportEventFeedTimestamp(reportEvent: ReportEvent): number {
    if (isVideoTimeRange(reportEvent.sourceType)) {
        return reportEvent.createdAt;
    }
    return getReportEventTime(reportEvent) ?? reportEvent.createdAt;
}

function reportEventKey(reportEvent: ReportEvent): string {
    return reportEvent.id;
}

function reportEventDescription(reportEvent: ReportEvent): string {
    return reportEvent.promptText;
}

function reportEventFeedItem(reportEvent: ReportEvent): Event {
    return {
        id: reportEvent.id,
        createdAt: reportEventFeedTimestamp(reportEvent),
        updatedAt: reportEvent.updatedAt,
        frames: reportEvent.thumbnailFrames,
        snapshotFrameId: reportEvent.thumbnailFrames[0]?.id ?? 0,
        description: reportEvent.description ?? reportEvent.promptText,
        classId: 0,
        sourceId: reportEvent.sourceId,
        sourceName: reportEvent.sourceName,
        sourceType: reportEvent.sourceType,
        thumbnailUrl: reportEvent.thumbnailUrls[0],
    };
}

export function ReportEventsFeed({
    reportId,
    titleContent,
    emptyState,
}: ReportEventsFeedProps) {
    const { t } = useTranslation([I18nNamespace.EVENTS, I18nNamespace.REPORTS]);
    const navigate = useNavigate();
    const { openReportEvent } = useOpenInNewWindow();
    const { events, isLoading, mutate } = useReportEventsData(reportId);
    const [searchParams] = useSearchParams();
    const sortBy = searchParams.get(URL_PARAM.SORT_BY);
    const sortOption = useMemo(() => parseSortOption(sortBy), [sortBy]);
    const orderedEvents = useMemo(
        () => sortReportEvents(events, sortOption),
        [events, sortOption],
    );
    const feedPlayingEventId = useEventFeedStore(
        (state) => state.feedPlayingEventId,
    );
    const selectFeedItem = useEventFeedStore((state) => state.feedOnItemSelect);

    const playNextOrLeaveReport = (removedEventId: string) => {
        if (removedEventId !== feedPlayingEventId) {
            return;
        }
        const removedIndex = orderedEvents.findIndex(
            (event) => event.id === removedEventId,
        );
        const hasRemainingEvents =
            removedIndex >= 0 && orderedEvents.length > 1;
        if (hasRemainingEvents && selectFeedItem) {
            const nextEvent =
                orderedEvents[(removedIndex + 1) % orderedEvents.length];
            selectFeedItem(nextEvent);
            return;
        }
        navigate(generatePath(AppRoutes.REPORT, { reportId }), {
            replace: true,
            state: { forceBackToReports: true },
        });
    };

    const {
        removeEventIdPending,
        removeInProgress,
        requestRemove,
        cancelRemove,
        confirmRemove,
    } = useReportEventRemoval(mutate, playNextOrLeaveReport);

    const actionMenuItems = useCallback(
        (reportEvent: ReportEvent) => [
            {
                key: 'removeFromReport',
                label: t('reports:detail.remove'),
                icon: TrashIcon,
                variant: 'danger' as const,
                onSelect: () => requestRemove(reportEvent.id),
            },
        ],
        [requestRemove, t],
    );

    const navigateToEvent = useCallback(
        (reportEvent: ReportEvent, clickEvent: MouseEvent<HTMLDivElement>) =>
            openReportEvent(reportEvent.id, clickEvent, {
                state: {
                    from: 'report-event',
                    feedContext: { type: 'reportEvents', reportId },
                },
            }),
        [openReportEvent, reportId],
    );

    return (
        <>
            <GenericEventFeed
                containerTitle={t('sidebar.reportEvents')}
                titleContent={titleContent}
                showViewAll={false}
                items={orderedEvents}
                isLoading={isLoading}
                emptyState={emptyState}
                itemKey={reportEventKey}
                itemEvent={reportEventFeedItem}
                cardTitle={t('page.event')}
                itemDescription={reportEventDescription}
                itemConfidence={getReportEventDisplayConfidence}
                itemTimeMetadata={reportEventTimeMetadata}
                itemActionMenuItems={actionMenuItems}
                onNavigate={navigateToEvent}
            />
            <RemoveReportEventConfirmationModal
                isOpen={removeEventIdPending !== undefined}
                removeInProgress={removeInProgress}
                onConfirm={confirmRemove}
                onCancel={cancelRemove}
            />
        </>
    );
}
